import Dexie, { type EntityTable } from 'dexie'
import type {
  CacheCarreteiro,
  CacheParteDiaria,
  CatApontador,
  CatDiametro,
  CatEncarregado,
  CatEquipe,
  CatEquipeEncarregado,
  CatLocalCarga,
  CatLocalDescargaRod,
  CatLocalDescargaUrb,
  CatMaterial,
  CatMaterialRede,
  CatMeta,
  CatMotorista,
  CatMve,
  CatObra,
  CatProjeto,
  CatProjetoServico,
  CatServico,
  CatTipoRede,
  CatTrecho,
  CatUnidade,
  DraftCarreteiroFicha,
  DraftParteDiaria,
  OutCarreteiro,
  OutFoto,
  OutParteDiaria,
  OutProducao,
  OutViagem,
  SessionProducaoDraft,
} from './entities'

/**
 * Dexie schema (IndexedDB). Replica as 25 tabelas Room do app Android.
 *
 * Convenções de índice:
 *  - `&id` = primary key
 *  - `&[a+b]` = composite PK
 *  - sufixo `+` = autoincrement (não usado aqui — IDs vêm do SIGA ou UUID v7)
 *  - `nome` em `cat_servico` para busca textual
 *  - `syncStatus` em out_* para queries do worker
 *
 * Migrações: incrementar `version(N)`. NÃO usar destructive cleanup como o Android —
 * usuário PWA pode ter outbox pendente há semanas.
 */

class MonitorDatabase extends Dexie {
  // Meta
  cat_meta!: EntityTable<CatMeta, 'catalogName'>

  // Catálogos
  cat_projeto!: EntityTable<CatProjeto, 'id'>
  cat_obra!: EntityTable<CatObra, 'id'>
  cat_servico!: EntityTable<CatServico, 'id'>
  cat_encarregado!: EntityTable<CatEncarregado, 'id'>
  cat_motorista!: EntityTable<CatMotorista, 'id'>
  cat_apontador!: EntityTable<CatApontador, 'id'>
  cat_equipe!: EntityTable<CatEquipe, 'id'>
  cat_unidade!: EntityTable<CatUnidade, 'id'>
  cat_material!: EntityTable<CatMaterial, 'id'>
  cat_material_rede!: EntityTable<CatMaterialRede, 'id'>
  cat_diametro!: EntityTable<CatDiametro, 'id'>
  cat_tipo_rede!: EntityTable<CatTipoRede, 'id'>
  cat_trecho!: EntityTable<CatTrecho, 'id'>
  cat_mve!: EntityTable<CatMve, 'id'>
  cat_local_carga!: EntityTable<CatLocalCarga, 'id'>
  cat_local_descarga_rod!: EntityTable<CatLocalDescargaRod, 'id'>
  cat_local_descarga_urb!: EntityTable<CatLocalDescargaUrb, 'id'>
  // PKs compostas: Dexie permite via string `[a+b]`. Tipo `'projetoId'` é placeholder
  // — Dexie API espera string do nome do índice primário; com PK composta usamos any.
  cat_projeto_servico!: Dexie.Table<CatProjetoServico, [number, number]>
  cat_equipe_encarregado!: Dexie.Table<CatEquipeEncarregado, [number, number, number]>

  // Outbox
  out_producao!: EntityTable<OutProducao, 'id'>
  out_carreteiro!: EntityTable<OutCarreteiro, 'id'>
  out_viagem!: EntityTable<OutViagem, 'id'>
  out_foto!: EntityTable<OutFoto, 'id'>

  // Sessão draft
  session_producao_draft!: EntityTable<SessionProducaoDraft, 'id'>
  draft_carreteiro!: EntityTable<DraftCarreteiroFicha, 'id'>

  // Cache offline do carreteiro (respostas que dependem de obra/caminhão/data)
  cache_carreteiro!: EntityTable<CacheCarreteiro, 'key'>

  // Parte Diária (MVE/EPP): outbox com linhas embutidas, rascunhos e cache offline
  out_parte_diaria!: EntityTable<OutParteDiaria, 'id'>
  draft_parte_diaria!: EntityTable<DraftParteDiaria, 'id'>
  cache_parte_diaria!: EntityTable<CacheParteDiaria, 'key'>

  constructor() {
    super('monitor')

    this.version(1).stores({
      cat_meta: '&catalogName',

      cat_projeto: '&id, codigo',
      cat_obra: '&id, projetoId, status, nome',
      cat_servico: '&id, tp, formulario, nome',
      cat_encarregado: '&id, nome',
      cat_motorista: '&id, nome',
      cat_apontador: '&id, nome',
      cat_equipe: '&id, nome',
      cat_unidade: '&id, nome',
      cat_material: '&id, nome',
      cat_material_rede: '&id, nome',
      cat_diametro: '&id, nome',
      cat_tipo_rede: '&id, nome',
      cat_trecho: '&id, projetoId, nome',
      cat_mve: '&id, tp, prefixo',
      cat_local_carga: '&id, projetoId',
      cat_local_descarga_rod: '&id, nome',
      cat_local_descarga_urb: '&id, nome',
      cat_projeto_servico: '&[projetoId+servicoId], projetoId, servicoId',
      cat_equipe_encarregado:
        '&[projetoId+equipeId+encarregadoId], projetoId, encarregadoId',

      out_producao: '&id, syncStatus, projetoId, capturedAt',
      out_carreteiro: '&id, syncStatus, obraId, projetoId, capturedAt',
      out_viagem: '&id, carreteiroId',
      out_foto: '&id, syncStatus, projetoId, capturedAt',

      session_producao_draft: '&id, createdAt',
    })

    // v2 — adiciona índice composto pra agrupar sessões por (projeto+dt+enc+equipe).
    // Dexie aceita índices em campos potencialmente null (são guardados como is undefined).
    // Aqui usamos `[projetoId+dt]` (sempre presentes) + filter in memory por encarregadoId/equipeId.
    this.version(2).stores({
      out_producao: '&id, syncStatus, projetoId, capturedAt, [projetoId+dt]',
    })

    // v3 — nenhum índice novo. `localPath` foi adicionado ao OutFoto (campo
    // opcional não-indexado) pra suportar storage no Filesystem nativo do
    // Capacitor Android. Dados existentes ficam como estão (continuam usando
    // `fileBlob`). Versão bumped pra registrar a evolução de schema mesmo
    // sem migration de dados.
    this.version(3).stores({
      out_foto: '&id, syncStatus, projetoId, capturedAt',
    })

    // v4 — carreteiro: novos campos não-indexados em cat_obra (tipoProjetoId/Nome,
    // chefeAdministrativoId/Nome), cat_servico (materialId/Nome) e cat_mve
    // (chefeEquipeId), vindos dos /catalogs/{obras,servicos,mves} atualizados.
    // OutCarreteiro/OutViagem ganharam campos opcionais (compat com outboxes antigos).
    //
    // CRÍTICO: a upgrade callback DELETA o cat_meta dos 3 catálogos afetados +
    // limpa as tabelas. Sem isso, usuários com cache antigo continuariam vendo
    // dados sem os novos campos (o cliente mandaria If-None-Match com o etag
    // antigo, mas o backend retorna etag novo, então o refresh aconteceria —
    // ainda assim limpamos pra garantir consistência mesmo se algo der errado
    // no fluxo de sync).
    this.version(4)
      .stores({
        cat_obra: '&id, projetoId, status, nome',
        cat_servico: '&id, tp, formulario, nome',
        out_carreteiro: '&id, syncStatus, obraId, projetoId, capturedAt',
        out_viagem: '&id, carreteiroId',
      })
      .upgrade(async (tx) => {
        await tx
          .table('cat_meta')
          .where('catalogName')
          .anyOf(['obras', 'servicos', 'mves'])
          .delete()
        await Promise.all([
          tx.table('cat_obra').clear(),
          tx.table('cat_servico').clear(),
          tx.table('cat_mve').clear(),
        ])
      })

    // v5 — cat_mve ganhou `placa` (vindo de cdt_mve.mve_chassi, normalizado em
    // UPPERCASE). Campo não-indexado, mas precisamos limpar o cache pra forçar
    // o próximo /catalogs/mves a popular o campo nas linhas existentes — sem
    // isso os caminhões já em cache aparecem com placa null até a próxima
    // mudança no ETag do backend.
    this.version(5)
      .stores({
        cat_mve: '&id, tp, prefixo',
      })
      .upgrade(async (tx) => {
        await tx.table('cat_meta').where('catalogName').equals('mves').delete()
        await tx.table('cat_mve').clear()
      })

    // v6 — Sprint 2 carreteiro:
    //  - cat_servico ganhou `contratoItemId` (PK do contrato vigente) — só
    //    preenchido no fetch carreteiro filtrado, mas precisa estar no schema.
    //  - out_viagem ganhou `cargaDtFixo` (DT fixo em km) — entrada do operador
    //    pra cálculo de produção. Default null em registros antigos.
    //  - Limpa cat_servico pra forçar re-fetch — a primeira ida online vai
    //    repopular com a coluna nova; fluxo offline-first continua: viagens já
    //    em outbox sobem com cargaDtFixo=null (backend trata).
    this.version(6)
      .stores({
        cat_servico: '&id, tp, formulario, nome',
        out_viagem: '&id, carreteiroId',
      })
      .upgrade(async (tx) => {
        await tx.table('cat_meta').where('catalogName').equals('servicos').delete()
        await tx.table('cat_servico').clear()
      })

    // v7 — locais de descarga ganharam `projetoId` (carreteiro filtra picker
    // pelo projeto da obra). Não-indexado, mas precisa refresh pra popular o
    // campo nos registros já cacheados.
    this.version(7)
      .stores({
        cat_local_descarga_rod: '&id, nome',
        cat_local_descarga_urb: '&id, nome',
      })
      .upgrade(async (tx) => {
        await tx
          .table('cat_meta')
          .where('catalogName')
          .anyOf(['locais_descarga_rod', 'locais_descarga_urb'])
          .delete()
        await Promise.all([
          tx.table('cat_local_descarga_rod').clear(),
          tx.table('cat_local_descarga_urb').clear(),
        ])
      })

    // v8 — locais ganharam `nome` composto pelo backend (origem+complemento
    // pra carga, complemento|rua+bairro|deposito pra descarga). Antes o
    // frontend caía no fallback "(local 12345)" expondo ID no picker.
    // Limpa o cache pra forçar re-fetch com o campo populado.
    // Também adiciona a tabela `draft_carreteiro` pra suportar múltiplas
    // fichas em paralelo (usuário pode ter N fichas em andamento, alternar
    // entre elas sem perder estado).
    this.version(8)
      .stores({
        cat_local_carga: '&id, projetoId',
        cat_local_descarga_rod: '&id, nome',
        cat_local_descarga_urb: '&id, nome',
        draft_carreteiro: '&id, obraId, mveId, dt, lastUpdatedAt',
      })
      .upgrade(async (tx) => {
        await tx
          .table('cat_meta')
          .where('catalogName')
          .anyOf(['locais_carga', 'locais_descarga_rod', 'locais_descarga_urb'])
          .delete()
        await Promise.all([
          tx.table('cat_local_carga').clear(),
          tx.table('cat_local_descarga_rod').clear(),
          tx.table('cat_local_descarga_urb').clear(),
        ])
      })

    // v9 — locais de carga "Usina" agora resolvem o nome pelo MVE referenciado
    // (mve_prefixo, ex.: "MVE.6293", "TER.1323") em vez de cair em "Usina sem
    // identificação". Limpa o cache + cat_meta com chaves "locais_carga_*"
    // (per-project) pra forçar re-fetch com o backend novo.
    this.version(9)
      .stores({
        cat_local_carga: '&id, projetoId',
      })
      .upgrade(async (tx) => {
        // cat_meta usa chaves "locais_carga_${projetoId}" — apaga todas.
        const metas = await tx.table('cat_meta').toArray()
        const locaisKeys = metas
          .filter((m: { catalogName: string }) => m.catalogName.startsWith('locais_carga'))
          .map((m: { catalogName: string }) => m.catalogName)
        if (locaisKeys.length > 0) {
          await tx
            .table('cat_meta')
            .where('catalogName')
            .anyOf(locaisKeys)
            .delete()
        }
        await tx.table('cat_local_carga').clear()
      })

    // v10 — módulo Produção: catálogos de rede passam a ser escopados por projeto
    // via cdt_projeto_tem_material_rede / cdt_projeto_tem_tipo_rede / cdt_projeto_tem_diametro.
    //  - Adiciona índice `projetoId` em cat_material_rede / cat_diametro / cat_tipo_rede.
    //  - cat_material agora carrega `unidadeNome` (vem do JOIN no backend).
    //  - Limpa o cache + cat_meta dos 4 catálogos pra forçar re-fetch com a
    //    nova estrutura (sem isso, registros antigos sem projetoId/unidadeNome
    //    seguem aparecendo).
    this.version(10)
      .stores({
        cat_material_rede: '&id, nome, projetoId',
        cat_diametro: '&id, nome, projetoId',
        cat_tipo_rede: '&id, nome, projetoId',
        cat_material: '&id, nome',
      })
      .upgrade(async (tx) => {
        // cat_meta usa chaves globais ("materiais_rede", etc) e per-project
        // ("materiais_rede_<projetoId>") — apaga ambos os formatos.
        const metas = await tx.table('cat_meta').toArray()
        const toDelete = metas
          .filter((m: { catalogName: string }) =>
            m.catalogName === 'materiais' ||
            m.catalogName.startsWith('materiais_rede') ||
            m.catalogName.startsWith('diametros') ||
            m.catalogName.startsWith('tipos_rede'),
          )
          .map((m: { catalogName: string }) => m.catalogName)
        if (toDelete.length > 0) {
          await tx.table('cat_meta').where('catalogName').anyOf(toDelete).delete()
        }
        await Promise.all([
          tx.table('cat_material').clear(),
          tx.table('cat_material_rede').clear(),
          tx.table('cat_diametro').clear(),
          tx.table('cat_tipo_rede').clear(),
        ])
      })

    // v11 — locais de carga/descarga: backend passa a resolver nome via
    // fornecedor (sys_pessoa.pessoa_nome) quando o local não tem complemento
    // (pedreiras, cantinas, almoxarifados, sedes, pistas etc). Cache local
    // antigo tem `nome: 'Pedreira sem identificação'` em ~152 linhas — precisa
    // refetch pra trocar pelo nome do fornecedor ("Pedreira — Goyaz Britas").
    this.version(11)
      .stores({
        cat_local_carga: '&id, projetoId',
        cat_local_descarga_rod: '&id, nome',
        cat_local_descarga_urb: '&id, nome',
      })
      .upgrade(async (tx) => {
        const metas = await tx.table('cat_meta').toArray()
        const toDelete = metas
          .filter((m: { catalogName: string }) =>
            m.catalogName === 'locais_descarga_rod' ||
            m.catalogName === 'locais_descarga_urb' ||
            m.catalogName.startsWith('locais_carga'),
          )
          .map((m: { catalogName: string }) => m.catalogName)
        if (toDelete.length > 0) {
          await tx.table('cat_meta').where('catalogName').anyOf(toDelete).delete()
        }
        await Promise.all([
          tx.table('cat_local_carga').clear(),
          tx.table('cat_local_descarga_rod').clear(),
          tx.table('cat_local_descarga_urb').clear(),
        ])
      })

    // v12 — alinhamento do carreteiro com o SIGA web: cache offline de chefe
    // da obra, caminhões por obra e serviços com contrato por caminhão.
    // out_carreteiro ganhou `rejeicao`/`draftState` e o status REJEITADA
    // (campos não indexados; `syncStatus` já é índice).
    this.version(12).stores({
      cache_carreteiro: '&key',
    })

    // v13 — módulo Parte Diária (2026-09-15): lançamento de MVE (mvt_mve, só
    // hoje, principal + frações) e EPP (mvt_equipamento_pequeno, período).
    // Uma linha do outbox = um POST; as linhas do dia ficam embutidas no objeto.
    this.version(13).stores({
      out_parte_diaria: '&id, syncStatus, mveId, dt, capturedAt',
      draft_parte_diaria: '&id, mveId, dt, lastUpdatedAt',
      cache_parte_diaria: '&key',
    })
  }
}

export const db = new MonitorDatabase()

/** Apaga completamente o banco — usado no logout (após `sessionStore.clear()`). */
export async function deleteDatabase(): Promise<void> {
  await db.delete()
  // Reabre conexão para próximo login
  await db.open()
}
